import {environment} from '../../environments/environment';
import {PageAttributes} from '../models/page-attributes';

const STAGING_ANALYTICS_CORE_SCRIPT_SRC = 'https://se-fe-analytics.ui.sestage.us/se-analytics-core/main.js';
const PRODUCTION_ANALYTICS_CORE_SCRIPT_SRC = 'https://se-fe-analytics.ui.sportngin.com/se-analytics-core/main.js';
const ANALYTICS_DEPTH1 = 'HQ';
const ANALYTICS_DEPTH2 = 'Commerce';

interface AnalyticsRouteSnapshot {
  params: Record<string, unknown>;
  data: Record<string, unknown>;
  firstChild?: AnalyticsRouteSnapshot | null;
}

/** Router snapshot shape used to build analytics page view attributes. */
export interface AnalyticsRouterStateSnapshot {
  url: string;
  root: AnalyticsRouteSnapshot;
}

/** Whether analytics debug logging and browser status helpers are enabled. */
export function isAnalyticsDebugEnabled(): boolean {
  return environment.enableAnalyticsDebug;
}

/** Resolves the se-analytics-core script URL for the current environment. */
export function getAnalyticsCoreScriptSrc(): string {
  return environment.production ? PRODUCTION_ANALYTICS_CORE_SCRIPT_SRC : STAGING_ANALYTICS_CORE_SCRIPT_SRC;
}

export function buildAnalyticsConfig() {
  return {
    platProp: 'se_project_management_ui',
    currentEnv: environment.production ? 'production' : 'staging',
  };
}

/**
 * Maps the active NgRx router snapshot to se-analytics page view attributes.
 */
export function buildPageViewAttributes(routerState: AnalyticsRouterStateSnapshot, orgName?: string): PageAttributes {
  const {params, frameRouterTitle} = collectRouteContext(routerState.root);
  const screenTitle = frameRouterTitle ?? inferScreenTitle(routerState.url);
  const organizationId = parseOrganizationId(String(params['currentOrgId'] ?? ''));

  return {
    depth1: ANALYTICS_DEPTH1,
    depth2: ANALYTICS_DEPTH2,
    depth3: screenTitle,
    page: routerState.url,
    title: screenTitle,
    boss_organization_id: organizationId,
    boss_organization_name: orgName,
  };
}

function collectRouteContext(root: AnalyticsRouteSnapshot): {
  params: Record<string, string>;
  frameRouterTitle?: string;
} {
  const params: Record<string, string> = {};
  let frameRouterTitle: string | undefined;
  let current: AnalyticsRouteSnapshot | null | undefined = root;

  while (current) {
    Object.assign(params, current.params);

    const routeTitle = current.data?.['frameRouterTitle'];
    if (typeof routeTitle === 'string') {
      frameRouterTitle = routeTitle;
    }

    current = current.firstChild;
  }

  return {params, frameRouterTitle};
}

function inferScreenTitle(url: string): string {
  const pathname = url.split('?')[0];

  if (pathname.includes('/clare-onboarding')) {
    return 'Clare Onboarding';
  }

  if (pathname.endsWith('/bundles/new')) {
    return 'Create product bundle';
  }

  if (/\/bundles\/[^/]+$/.test(pathname) && !pathname.endsWith('/bundles')) {
    return 'Bundle detail';
  }

  if (pathname.endsWith('/bundles')) {
    return 'Bundles';
  }

  if (pathname.endsWith('/products/new')) {
    return 'Add product';
  }

  if (/\/products\/[^/]+$/.test(pathname) && !pathname.endsWith('/products')) {
    return 'Product detail';
  }

  if (pathname.includes('/catalogs/') && !pathname.endsWith('/catalogs')) {
    return 'Catalog detail';
  }

  if (pathname.endsWith('/catalogs')) {
    return 'Catalogs';
  }

  if (pathname.endsWith('/not-found')) {
    return 'Not found';
  }

  return 'Products';
}

function parseOrganizationId(orgId: string): number | undefined {
  if (!orgId) {
    return undefined;
  }

  const parsed = Number(orgId);
  return Number.isFinite(parsed) ? parsed : undefined;
}
